import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, AuthContextType, AuthProviderProps } from './types';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from './utils/api'


const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: AuthProviderProps) => {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
    const navigate = useNavigate();

    const isAuthenticated = !!token;

    useEffect(() => {
        const validateToken = async () => {
            const storedToken = localStorage.getItem('token');
            if (storedToken) {
                try {
                    // Make a request to a protected route to validate the token
                    const response = await fetch(`${API_BASE_URL}/validate-token`, {
                        headers: {
                            'Authorization': `Bearer ${storedToken}`,
                        },
                    });

                    if (response.ok) {
                        const user = await response.json();
                        setUser(user);
                        setToken(storedToken);
                    } else {
                        // Token is invalid or expired
                        logout();
                    }
                } catch (error) {
                    console.error('Token validation failed', error);
                    logout();
                }
            }
        };

        validateToken();
    }, []);

    const login = async (username: string, password: string): Promise<void> => {
        try {
            const response = await fetch(`${API_BASE_URL}/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password }),
            });

            if (response.ok) {
                const { token, user } = await response.json();
                setToken(token);
                localStorage.setItem('token', token);
                // Here you would typically decode the token to get user details
                // For simplicity, we'll just set the username
                setUser(user as User);
                navigate('/');
            } else {
                throw new Error('Login failed');
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const logout = () => {
        setUser(null);
        setToken(null);
        localStorage.removeItem('token');
        navigate('/login');
    };

    return (
        <AuthContext.Provider value={{ user, isAuthenticated, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};